import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DM_MAX_CHARS, type ConversationView, type DmMessageView } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { fetchConversations, invalidateMessages, markRead, messageKeys, openConversation, sendMessage, useConversationsQuery, useMessagesQuery } from "../../api/messages";
import { muteActor, unmuteActor } from "../../safety/mutes";
import { useTownStore } from "../../state/store";
import { Dialog } from "../Dialog";
import { FlagIcon } from "../FlagIcon";
import { JoinFriendButton } from "../worlds/JoinFriend";
import { Avatar, ConversationRow, MessageBubbles, MessagesEmptyState, MessagesIllustration } from "./messengerParts";

export { previewOf } from "./messengerParts";

/** The safety line every conversation shows (ADR-0014). */
export const DM_SAFETY_TEXT =
  "Private messages are between the two of you. Townsy moderators read a conversation only if someone reports it. Messages are kept 30 days. Never share passwords, codes or payment details.";

/** What "Show me how" (empty messages) tells: how a conversation starts (the person card's Message button). */
export const DM_HOW_TEXT = "Click (or tap) anyone walking in town, or their name tag. Their card has a Message button that starts a private conversation.";

/** Characters left before the counter warns (the box stops at DM_MAX_CHARS). */
const COUNTER_WARN_AT = DM_MAX_CHARS - 50;

/** The messages dialog host (in town): opens on request, and turns realtime notices into toasts and fresh lists. */
export function MessagesHost() {
  const qc = useQueryClient();
  const request = useTownStore((s) => s.ui.messages);
  const enabled = useTownStore((s) => Boolean(s.bootstrap?.features.privateMessagesEnabled && s.bootstrap.session.userId));
  const ping = useTownStore((s) => s.dmPing);
  const lastPing = useRef(0);
  useEffect(() => {
    if (!enabled || !ping || ping.seq === lastPing.current) return;
    lastPing.current = ping.seq;
    invalidateMessages(qc, ping.conversationId);
    const S = useTownStore.getState();
    if (S.ui.messages?.conversationId === ping.conversationId) return;
    void qc.fetchQuery({ queryKey: messageKeys.conversations, queryFn: fetchConversations, staleTime: 0 }).then((list) => {
      const c = list.items.find((x) => x.conversationId === ping.conversationId);
      if (!c || c.mutedByMe) return;
      useTownStore.getState().pushToast(`New private message from ${c.other.label}.`, "info", { label: "Open", run: () => useTownStore.getState().openMessages({ conversationId: c.conversationId }) });
    }, () => undefined);
  }, [enabled, ping, qc]);
  if (!enabled || !request) return null;
  return <MessagesDialog key={request.seq} conversationId={request.conversationId} recipientActorId={request.recipientActorId} onClose={() => useTownStore.getState().closeMessages()} />;
}

/**
 * A two-pane messenger (founder feedback 2026-10-14): conversations on the left (initials avatar, last message, time,
 * unread count), the open conversation on the right (bubbles by day, a composer that stays at the bottom). On a phone
 * one pane shows at a time, with a back button. Safety stays as in ADR-0014: mute, report (the conversation or one
 * message), the safety line, plain text only.
 */
export function MessagesDialog({ conversationId, recipientActorId, onClose }: { conversationId: string | null; recipientActorId: string | null; onClose(): void }) {
  const qc = useQueryClient();
  const [active, setActive] = useState<string | null>(conversationId);
  const [pane, setPane] = useState<"list" | "thread">(conversationId || recipientActorId ? "thread" : "list");
  const [opening, setOpening] = useState<string | null>(recipientActorId ? "Opening the conversation…" : null);
  const [openError, setOpenError] = useState<string | null>(null);
  const list = useConversationsQuery(true);

  useEffect(() => {
    if (!recipientActorId) return;
    let live = true;
    openConversation(recipientActorId).then(
      (c) => {
        if (!live) return;
        setActive(c.conversationId);
        setOpening(null);
        invalidateMessages(qc);
      },
      (err: unknown) => {
        if (!live) return;
        setOpening(null);
        setOpenError(errorMessage(err, "This conversation could not be opened."));
      },
    );
    return () => {
      live = false;
    };
  }, [recipientActorId, qc]);

  const items = list.data?.items ?? [];
  const current = items.find((c) => c.conversationId === active) ?? null;
  const empty = !list.isPending && !list.error && items.length === 0 && !opening && !openError && !active;
  const showHow = () => {
    onClose();
    useTownStore.getState().pushToast(DM_HOW_TEXT, "info", { label: "Got it", run: () => undefined });
  };
  return (
    <Dialog title="Private messages" onClose={onClose} wide>
      <div className={`msgr${empty ? " msgr-is-empty" : ""}`} data-pane={pane} data-testid="dm-dialog">
        {empty ? (
          <MessagesEmptyState onShowHow={showHow} />
        ) : (
          <>
            <nav className="msgr-list" aria-label="Conversations">
              <div className="msgr-list-head">
                <span>Conversations</span>
                {list.data && list.data.unreadTotal > 0 ? <span className="msgr-list-unread">{list.data.unreadTotal} unread</span> : null}
              </div>
              {list.isPending ? <p className="msgr-list-note">Loading…</p> : null}
              {list.error ? <p className="msgr-list-note error-text">{errorMessage(list.error, "Your conversations could not be loaded.")}</p> : null}
              {!list.isPending && !list.error && !items.length ? <p className="msgr-list-note">No conversations yet. Click a person in town and choose Message.</p> : null}
              <ul>
                {items.map((c) => (
                  <li key={c.conversationId}>
                    <ConversationRow
                      conversation={c}
                      active={c.conversationId === active}
                      onSelect={() => {
                        setActive(c.conversationId);
                        setPane("thread");
                      }}
                    />
                  </li>
                ))}
              </ul>
            </nav>
            <section className="msgr-thread" aria-label={current ? `Conversation with ${current.other.label}` : "Conversation"}>
              {current ? (
                <Thread conversation={current} onBack={() => setPane("list")} />
              ) : (
                <div className="msgr-placeholder">
                  <button type="button" className="btn btn-ghost btn-sm msgr-back msgr-back-floating" onClick={() => setPane("list")}>
                    ‹ Conversations
                  </button>
                  {opening ? (
                    <p className="msgr-placeholder-text" role="status">
                      <span className="msgr-spinner" aria-hidden="true" /> {opening}
                    </p>
                  ) : openError ? (
                    <div className="notice notice-info small" role="alert" data-testid="dm-open-error">
                      {openError}
                    </div>
                  ) : (
                    <>
                      <MessagesIllustration size={132} />
                      <p className="msgr-placeholder-text">Choose a conversation to read it here.</p>
                    </>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </Dialog>
  );
}

function Thread({ conversation: c, onBack }: { conversation: ConversationView; onBack(): void }) {
  const qc = useQueryClient();
  const messages = useMessagesQuery(c.conversationId);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const items: DmMessageView[] = messages.data?.items ?? [];
  const isGuest = useTownStore((s) => !s.bootstrap?.session.userId);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [items.length, c.conversationId]);

  // Opening a conversation with unread messages marks it read.
  useEffect(() => {
    if (c.unreadCount === 0) return;
    void markRead(c.conversationId).then(() => invalidateMessages(qc), () => undefined);
  }, [c.conversationId, c.unreadCount, qc]);

  // The box grows with the text (up to about five lines), then scrolls.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [text]);

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    try {
      await sendMessage(c.conversationId, t, `dm-${crypto.randomUUID()}`);
      setText("");
      invalidateMessages(qc, c.conversationId);
    } catch (err) {
      setError(errorMessage(err, "The message was not sent. Try again."));
      invalidateMessages(qc, c.conversationId);
    } finally {
      setBusy(false);
      // Back to the box (the Send button was disabled while sending, which drops focus out of the dialog): type the
      // next message, and Escape still closes the dialog.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };
  const S = useTownStore.getState;
  const report = (m?: DmMessageView) =>
    S().openPersonReport({
      actorId: c.other.actorId,
      label: c.other.label,
      conversationId: c.conversationId,
      ...(m ? { message: { messageId: m.messageId, text: m.text, at: m.at } } : {}),
    });
  const counterTone = text.length >= DM_MAX_CHARS ? " over" : text.length >= COUNTER_WARN_AT ? " near" : "";
  return (
    <div className="msgr-thread-inner" data-testid="dm-thread" data-conversation-id={c.conversationId}>
      <header className="msgr-head">
        <button type="button" className="btn btn-ghost icon-btn msgr-back" aria-label="Back to conversations" onClick={onBack}>
          ‹
        </button>
        <Avatar label={c.other.label} size={38} />
        <div className="msgr-head-who">
          <strong className="msgr-head-name">
            <FlagIcon code={c.other.countryCode} height={14} /> {c.other.label}
          </strong>
          <span className="msgr-head-sub">{c.mutedByMe ? "Muted: their messages are hidden from your notices" : "Private conversation · verified account"}</span>
          {/* ADR-0043: "Join them in Finch", only when the server shows which world they are in (their setting). */}
          {c.mutedByMe ? null : <JoinFriendButton actorId={c.other.actorId} />}
        </div>
        <div className="msgr-head-actions">
          <button
            type="button"
            className="btn btn-sm"
            aria-pressed={c.mutedByMe}
            onClick={async () => {
              try {
                if (c.mutedByMe) await unmuteActor(c.other.actorId, !isGuest);
                else await muteActor(c.other.actorId, !isGuest);
              } catch (err) {
                setError(errorMessage(err, "That did not work."));
              }
              invalidateMessages(qc, c.conversationId);
            }}
            data-testid="dm-mute"
          >
            {c.mutedByMe ? "Unmute" : "Mute"}
          </button>
          <button type="button" className="btn btn-sm btn-ghost msgr-report-conv" onClick={() => report()} data-testid="dm-report">
            Report…
          </button>
        </div>
      </header>
      <p className="msgr-safety">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <rect x="5" y="10.5" width="14" height="10" rx="2" />
          <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
        </svg>
        <span>{DM_SAFETY_TEXT}</span>
      </p>
      <ol className="msgr-messages" ref={listRef} aria-live="polite" data-testid="dm-messages">
        {messages.isPending ? <li className="msgr-day" role="presentation"><span>Loading…</span></li> : null}
        {!messages.isPending && !items.length ? (
          <li className="msgr-first" role="presentation">
            <Avatar label={c.other.label} size={52} />
            <span>
              This is the start of your conversation with <strong>{c.other.label}</strong>. Say hello!
            </span>
          </li>
        ) : null}
        <MessageBubbles messages={items} otherLabel={c.other.label} onReport={(m) => report(m)} />
      </ol>
      {error ? (
        <div className="msgr-error error-text small" role="alert">
          {error}
        </div>
      ) : null}
      {c.canSend ? (
        <form
          className="msgr-compose"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <div className="msgr-compose-row">
            <textarea
              ref={inputRef}
              rows={1}
              value={text}
              maxLength={DM_MAX_CHARS}
              placeholder={`Message ${c.other.label}`}
              aria-label={`Message ${c.other.label}`}
              aria-describedby={`dm-count-${c.conversationId}`}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              data-testid="dm-input"
            />
            <button type="submit" className="btn btn-primary msgr-send" disabled={busy || !text.trim()} data-testid="dm-send">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <path d="M4 12 20 4l-5 16-3.5-6.5z" />
                <path d="m11.5 13.5 4-4" />
              </svg>
              <span>{busy ? "Sending…" : "Send"}</span>
            </button>
          </div>
          <div className="msgr-compose-foot">
            <span>Plain text · links are not clickable · Enter sends, Shift+Enter adds a line</span>
            <span className={`msgr-count${counterTone}`} id={`dm-count-${c.conversationId}`} aria-live="polite">
              {text.length}/{DM_MAX_CHARS}
            </span>
          </div>
        </form>
      ) : (
        <div className="msgr-cannot" role="note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="8.5" />
            <path d="m6 6 12 12" />
          </svg>
          <span data-testid="dm-cannot-send">{c.cannotSendReason ?? "You can't send messages here right now."}</span>
        </div>
      )}
    </div>
  );
}
